import { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import type { Deal } from "../../lib/types";
import DealLink from "../DealLink";
import DealAgeChips from "../shared/DealAgeChips";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

/**
 * Drone Flights Tab — Sam's drone stage pipeline.
 * Shows all deals in drone-phase stages as a kanban-style stage pipeline,
 * with stage chips at top for filtering and counts.
 * Includes ••• card actions (stage change, RM reassignment, delete)
 * and mass update (select multiple → batch stage change).
 */

/** Active drone stages shown by default (excludes terminal stages) */
const ACTIVE_DRONE_STAGES = [
  "pending_asm_review",
  "appointment_received",
  "drone_scheduled",
  "drone_complete",
  "report_in_progress",
  "qc_review",
  "report_approved",
  "needs_climb_inspection",
] as const;

/** Terminal / archived drone stages (hidden by default, toggle to show) */
const ARCHIVED_DRONE_STAGES = ["insufficient_damage"] as const;

const DRONE_STAGES = [
  ...ACTIVE_DRONE_STAGES,
  ...ARCHIVED_DRONE_STAGES,
] as const;

const STAGE_LABELS: Record<string, string> = {
  pending_asm_review: "Pending ASM Review",
  appointment_received: "Appointment Received",
  drone_scheduled: "Drone Scheduled",
  drone_complete: "Drone Complete",
  report_in_progress: "Report In Progress",
  qc_review: "QC Review",
  report_approved: "Report Approved",
  needs_climb_inspection: "Needs Climb",
  insufficient_damage: "Insufficient Damage",
  // Non-drone stages for the stage-change dropdown
  initial_contact: "Initial Contact",
  follow_up: "Follow Up",
  appointment_set: "Appointment Set",
  no_thanks: "No Thanks",
  claim_filed: "Claim Filed",
  closed_lost: "Closed Lost",
  cancelled: "Cancelled",
};

const STAGE_COLORS: Record<string, string> = {
  pending_asm_review: "bg-slate-200 text-slate-700",
  appointment_received: "bg-blue-100 text-blue-700",
  drone_scheduled: "bg-indigo-100 text-indigo-700",
  drone_complete: "bg-emerald-100 text-emerald-700",
  report_in_progress: "bg-amber-100 text-amber-700",
  qc_review: "bg-purple-100 text-purple-700",
  report_approved: "bg-emerald-200 text-emerald-800",
  needs_climb_inspection: "bg-orange-100 text-orange-700",
  insufficient_damage: "bg-red-100 text-red-700",
};

// All stages grouped for the dropdown
const ALL_STAGE_OPTIONS: { phase: string; stages: string[] }[] = [
  { phase: "Drone", stages: [...DRONE_STAGES] },
  { phase: "Leads", stages: ["initial_contact", "follow_up", "appointment_set", "no_thanks", "claim_filed"] },
  { phase: "Claims", stages: ["assign_roof_specialist", "pre_inspection_ready", "adjuster_meeting_ready", "waiting_on_claims"] },
  { phase: "Builds", stages: ["scope_review", "install_meeting", "build_submitted", "schedule_build"] },
  { phase: "Closing", stages: ["build_in_progress", "final_walk_scheduled", "final_walk_complete", "commission_processing", "supplement_in_progress", "final_collection", "warranty_registration", "closed_won"] },
  { phase: "Other", stages: ["closed_lost", "cancelled"] },
];

// Phase lookup from stage
function phaseForStage(stage: string): string {
  const droneStages = [...DRONE_STAGES] as string[];
  if (droneStages.includes(stage)) return "drone";
  const leadsStages = ["initial_contact", "follow_up", "appointment_set", "no_thanks", "claim_filed"];
  if (leadsStages.includes(stage)) return "leads";
  const claimsStages = ["assign_roof_specialist", "pre_inspection_ready", "adjuster_meeting_ready", "waiting_on_claims"];
  if (claimsStages.includes(stage)) return "claims";
  const reinspStages = ["reinspection_docs_needed", "reinspection_ready", "reinspection_filed"];
  if (reinspStages.includes(stage)) return "reinspections";
  const buildsStages = ["scope_review", "install_meeting", "build_submitted", "schedule_build"];
  if (buildsStages.includes(stage)) return "builds";
  if (["closed_won", "closed_lost", "cancelled"].includes(stage)) return "closed";
  return "closing";
}

type DroneRow = Pick<
  Deal,
  | "id"
  | "homeowner_name"
  | "property_street"
  | "property_city"
  | "stage"
  | "days_in_stage"
  | "stage_entered_at"
  | "phase_entered_at"
  | "owner_id"
  | "split_rm_id"
  | "appointment_date"
  | "drone_scheduled_date"
  | "drone_completed_date"
  | "assigned_drone_pilot_id"
  | "drone_sla_met"
  | "drone_unable_to_fly"
  | "imging_report_link"
  | "asm_review_status"
  | "report_qc_status"
  | "created_at"
>;

const COLS =
  "id,homeowner_name,property_street,property_city,stage,days_in_stage,stage_entered_at,phase_entered_at,owner_id,split_rm_id,appointment_date,drone_scheduled_date,drone_completed_date,assigned_drone_pilot_id,drone_sla_met,drone_unable_to_fly,imging_report_link,asm_review_status,report_qc_status,created_at";

export default function DroneFlightsTab() {
  const { member: _member, role: userRole } = useAuth();
  const canUseActions = !!userRole && ["admin", "sales_director", "asm"].includes(userRole);
  const [deals, setDeals] = useState<DroneRow[] | null>(null);
  const [members, setMembers] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const [activeStage, setActiveStage] = useState<string | null>(null);
  const [skipBusy, setSkipBusy] = useState<Record<string, boolean>>({});
  const [showArchived, setShowArchived] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Mass update state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [massStage, setMassStage] = useState("");
  const [massUpdating, setMassUpdating] = useState(false);

  // Card action state (per-card)
  const [showActionsId, setShowActionsId] = useState<string | null>(null);
  const [cardBusy, setCardBusy] = useState<Record<string, boolean>>({});

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const queryStages = showArchived
        ? [...DRONE_STAGES]
        : [...ACTIVE_DRONE_STAGES];
      const orFilter = queryStages.map((s) => `stage.eq.${s}`).join(",");
      const [{ data, error }, { data: mems }] = await Promise.all([
        supabase
          .from("deals")
          .select(COLS)
          .or(orFilter)
          .order("created_at", { ascending: true }),
        supabase
          .from("team_roster")
          .select("id,full_name")
          .eq("is_active", true),
      ]);
      if (error) {
        if (!cancelled) setErr(error.message);
        return;
      }
      if (!cancelled) {
        setDeals((data ?? []) as unknown as DroneRow[]);
        const map: Record<string, string> = {};
        (mems ?? []).forEach((m) => {
          map[m.id] = m.full_name;
        });
        setMembers(map);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey, showArchived]);

  // Stage counts
  const stageCounts = useMemo(() => {
    if (!deals) return {};
    const counts: Record<string, number> = {};
    for (const d of deals) {
      const s = d.stage ?? "unknown";
      counts[s] = (counts[s] || 0) + 1;
    }
    return counts;
  }, [deals]);

  // Filtered deals
  const filtered = useMemo(() => {
    if (!deals) return [];
    if (!activeStage) return deals;
    return deals.filter((d) => d.stage === activeStage);
  }, [deals, activeStage]);

  // SLA check: 3 days from appointment
  function slaStatus(d: DroneRow): { label: string; color: string } {
    if (d.drone_completed_date) return { label: "Done", color: "text-emerald-600" };
    if (!d.appointment_date) return { label: "No appt", color: "text-slate-400" };
    const appt = new Date(d.appointment_date);
    const now = new Date();
    const daysSince = Math.floor(
      (now.getTime() - appt.getTime()) / 86_400_000,
    );
    if (daysSince > 3)
      return { label: `${daysSince}d (OVERDUE)`, color: "text-red-600 font-semibold" };
    if (daysSince === 3)
      return { label: "3d (DUE TODAY)", color: "text-amber-600 font-semibold" };
    return { label: `${daysSince}d`, color: "text-slate-500" };
  }

  async function onSkipDrone(d: DroneRow) {
    if (skipBusy[d.id]) return;
    if (!window.confirm(`Skip drone and send "${d.homeowner_name ?? "(no name)"}" straight to Initial Contact?`)) return;
    setSkipBusy((b) => ({ ...b, [d.id]: true }));
    try {
      const { error } = await supabase
        .from("deals")
        .update({ stage: "initial_contact", phase: "leads", days_in_stage: 0 })
        .eq("id", d.id);
      if (error) throw error;
      reload();
    } catch (e: any) {
      alert(`Update failed: ${e.message ?? e}`);
    } finally {
      setSkipBusy((b) => ({ ...b, [d.id]: false }));
    }
  }

  // Card-level stage change
  async function handleCardStageChange(dealId: string, newStage: string, currentStage: string | null) {
    if (!newStage || newStage === currentStage) return;
    setCardBusy((b) => ({ ...b, [dealId]: true }));
    try {
      const newPhase = phaseForStage(newStage);
      const { error } = await supabase
        .from("deals")
        .update({ stage: newStage, phase: newPhase, days_in_stage: 0 })
        .eq("id", dealId);
      if (error) throw error;
      reload();
    } catch (e: any) {
      alert(`Stage update failed: ${e.message ?? e}`);
    } finally {
      setCardBusy((b) => ({ ...b, [dealId]: false }));
    }
  }

  // Card-level delete
  async function handleCardDelete(dealId: string, name: string | null) {
    if (!window.confirm(`Delete "${name ?? "(no name)"}" permanently? This cannot be undone.`)) return;
    setCardBusy((b) => ({ ...b, [dealId]: true }));
    try {
      // All child FK references cascade on delete
      const { error } = await supabase.from("deals").delete().eq("id", dealId);
      if (error) throw error;
      reload();
    } catch (e: any) {
      alert(`Delete failed: ${e.message ?? e}`);
    } finally {
      setCardBusy((b) => ({ ...b, [dealId]: false }));
    }
  }

  // Card-level RM change
  async function handleRmChange(dealId: string, field: "owner_id" | "split_rm_id", val: string | null) {
    const { error } = await supabase.from("deals").update({ [field]: val }).eq("id", dealId);
    if (error) { alert(`Update failed: ${error.message}`); return; }
    reload();
  }

  // Mass update
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  function exitSelectMode() {
    setSelectMode(false);
    setSelectedIds(new Set());
    setMassStage("");
  }

  async function applyMassUpdate() {
    if (!massStage || selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(`Move ${count} deal${count > 1 ? "s" : ""} to ${STAGE_LABELS[massStage] ?? massStage}?`)) return;
    setMassUpdating(true);
    try {
      const ids = Array.from(selectedIds);
      const newPhase = phaseForStage(massStage);
      const BATCH = 50;
      for (let i = 0; i < ids.length; i += BATCH) {
        const chunk = ids.slice(i, i + BATCH);
        const { error } = await supabase
          .from("deals")
          .update({ stage: massStage, phase: newPhase, days_in_stage: 0 })
          .in("id", chunk);
        if (error) throw error;
      }
      exitSelectMode();
      reload();
    } catch (e: any) {
      alert(`Mass update failed: ${e.message ?? e}`);
    } finally {
      setMassUpdating(false);
    }
  }

  if (err) return <div className="bg-red-50 text-red-700 p-4 rounded">{err}</div>;
  if (!deals)
    return (
      <div className="text-slate-500 py-8 text-center text-sm">
        Loading drone flights…
      </div>
    );

  const total = deals.length;

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center gap-3 bg-white rounded-xl shadow px-4 py-3">
        <span className="text-lg font-semibold text-bardin-ink">{total}</span>
        <span className="text-sm text-slate-500">deals in drone pipeline</span>
        <div className="ml-auto flex items-center gap-3">
          <button
            onClick={() => { setShowArchived((v) => !v); setActiveStage(null); }}
            className={
              "text-xs font-medium px-3 py-1.5 rounded-lg transition " +
              (showArchived
                ? "bg-red-100 text-red-700"
                : "bg-slate-100 text-slate-500 hover:bg-slate-200")
            }
          >
            {showArchived ? "Hide Insufficient Damage" : "Show Insufficient Damage"}
          </button>
          <button
            onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
            className={
              "text-xs font-medium px-3 py-1.5 rounded-lg transition " +
              (selectMode
                ? "bg-bardin-ink text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200")
            }
          >
            {selectMode ? "Cancel Select" : "Select"}
          </button>
          <span className="text-xs text-slate-400">3-day SLA</span>
        </div>
      </div>

      {/* Stage chips — scrollable */}
      <div className="overflow-x-auto -mx-1 px-1">
        <div className="flex gap-1.5 min-w-max">
          <button
            onClick={() => setActiveStage(null)}
            className={
              "px-2.5 py-1.5 rounded-full text-xs font-medium transition " +
              (!activeStage
                ? "bg-bardin-ink text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200")
            }
          >
            All ({total})
          </button>
          {(showArchived ? DRONE_STAGES : ACTIVE_DRONE_STAGES).map((stage) => {
            const count = stageCounts[stage] || 0;
            const isActive = activeStage === stage;
            return (
              <button
                key={stage}
                onClick={() => setActiveStage(isActive ? null : stage)}
                className={
                  "px-2.5 py-1.5 rounded-full text-xs font-medium transition whitespace-nowrap " +
                  (isActive
                    ? "bg-bardin-teal text-white"
                    : count > 0
                      ? STAGE_COLORS[stage] + " hover:opacity-80"
                      : "bg-slate-50 text-slate-400")
                }
              >
                {STAGE_LABELS[stage]} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Deal cards */}
      <div className="space-y-2">
        {filtered.length === 0 && (
          <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
            No deals{activeStage ? ` at ${STAGE_LABELS[activeStage]}` : ""}.
          </div>
        )}

        {filtered.map((d) => {
          const sla = slaStatus(d);
          const ownerName = d.owner_id ? members[d.owner_id] : null;
          const pilotName = d.assigned_drone_pilot_id
            ? members[d.assigned_drone_pilot_id]
            : null;
          const isCardActionsOpen = showActionsId === d.id;
          const isBusy = !!cardBusy[d.id];

          return (
            <div
              key={d.id}
              className={
                "bg-white rounded-xl shadow px-4 py-3 space-y-1.5 " +
                (selectMode && selectedIds.has(d.id) ? "ring-2 ring-bardin-teal bg-bardin-sky/10" : "")
              }
              onClick={selectMode ? () => toggleSelect(d.id) : undefined}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1 flex items-start gap-2">
                  {selectMode && (
                    <div className="flex-shrink-0 pt-0.5" onClick={(e) => { e.stopPropagation(); toggleSelect(d.id); }}>
                      <div
                        className={
                          "w-5 h-5 rounded border-2 flex items-center justify-center transition " +
                          (selectedIds.has(d.id)
                            ? "bg-bardin-teal border-bardin-teal"
                            : "border-slate-300 bg-white hover:border-bardin-teal/50")
                        }
                      >
                        {selectedIds.has(d.id) && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-bardin-ink">
                        <DealLink id={d.id}>
                          {d.homeowner_name ?? "(no name)"}
                        </DealLink>
                      </span>
                      <span
                        className={
                          "text-[10px] font-medium px-2 py-0.5 rounded-full " +
                          (STAGE_COLORS[d.stage ?? ""] ?? "bg-slate-100 text-slate-600")
                        }
                      >
                        {STAGE_LABELS[d.stage ?? ""] ?? d.stage}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {isAddressLinkable(d) ? (
                        <a
                          href={mapsHref(d)}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="hover:underline hover:text-slate-700"
                        >
                          {[d.property_street, d.property_city].filter(Boolean).join(", ")}
                        </a>
                      ) : (
                        [d.property_street, d.property_city].filter(Boolean).join(", ") || "—"
                      )}
                    </div>
                    {ownerName && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        RM: {ownerName}
                        {d.split_rm_id && members[d.split_rm_id] && <> &amp; {members[d.split_rm_id]}</>}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className={`text-xs ${sla.color}`}>{sla.label}</div>
                  <DealAgeChips deal={d} align="right" className="mt-1" />
                </div>
              </div>

              {/* Detail row */}
              <div className="flex items-center gap-3 flex-wrap text-xs text-slate-500">
                {pilotName && (
                  <span className="text-indigo-600">
                    Pilot: {pilotName.split(" ")[0]}
                  </span>
                )}
                {d.appointment_date && (
                  <span>
                    Appt:{" "}
                    {new Date(d.appointment_date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                )}
                {d.drone_scheduled_date && (
                  <span className="text-indigo-600">
                    Fly:{" "}
                    {new Date(d.drone_scheduled_date).toLocaleDateString(
                      "en-US",
                      { month: "short", day: "numeric" },
                    )}
                  </span>
                )}
                {d.drone_completed_date && (
                  <span className="text-emerald-600">
                    Flown:{" "}
                    {new Date(d.drone_completed_date).toLocaleDateString(
                      "en-US",
                      { month: "short", day: "numeric" },
                    )}
                  </span>
                )}
                {d.drone_unable_to_fly && (
                  <span className="text-red-600 font-medium">Unable to fly</span>
                )}
                {d.imging_report_link && (
                  <span className="text-emerald-600">Report linked</span>
                )}
              </div>

              {/* Skip drone → lead button */}
              {!selectMode && (
                <button
                  onClick={() => onSkipDrone(d)}
                  disabled={!!skipBusy[d.id]}
                  className="self-start px-3 py-1 text-xs font-medium rounded-lg bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors disabled:opacity-50"
                >
                  {skipBusy[d.id] ? "Moving…" : "No Drone → Lead"}
                </button>
              )}

              {/* ••• Action row — exec team only */}
              {!selectMode && canUseActions && (
                <div className="pt-1 border-t border-slate-100 mt-1">
                  {!isCardActionsOpen ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowActionsId(d.id); }}
                      className="text-[10px] text-slate-400 hover:text-slate-600 transition px-1"
                      title="Quick actions"
                    >
                      •••
                    </button>
                  ) : (
                    <div className="flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          defaultValue={d.stage ?? ""}
                          onChange={(e) => handleCardStageChange(d.id, e.target.value, d.stage)}
                          disabled={isBusy}
                          className="text-xs border border-slate-200 rounded px-2 py-1 bg-white text-slate-700 max-w-[180px] disabled:opacity-50"
                        >
                          {ALL_STAGE_OPTIONS.map((group) => (
                            <optgroup key={group.phase} label={group.phase}>
                              {group.stages.map((s) => (
                                <option key={s} value={s}>
                                  {STAGE_LABELS[s] ?? s}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        <button
                          onClick={() => handleCardDelete(d.id, d.homeowner_name)}
                          disabled={isBusy}
                          className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition disabled:opacity-50"
                        >
                          Delete
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowActionsId(null); }}
                          className="text-xs text-slate-400 hover:text-slate-600 px-1"
                        >
                          ✕
                        </button>
                      </div>
                      {/* RM / Split RM reassignment */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <label className="text-[10px] text-slate-400">RM:</label>
                        <select
                          value={d.owner_id ?? ""}
                          onChange={(e) => handleRmChange(d.id, "owner_id", e.target.value || null)}
                          className="text-xs border border-slate-200 rounded px-2 py-1 bg-white text-slate-700 max-w-[150px]"
                        >
                          <option value="">(none)</option>
                          {Object.entries(members)
                            .sort(([, a], [, b]) => a.localeCompare(b))
                            .map(([id, name]) => (
                              <option key={id} value={id}>{name}</option>
                            ))}
                        </select>
                        <label className="text-[10px] text-slate-400 ml-1">Split:</label>
                        <select
                          value={d.split_rm_id ?? ""}
                          onChange={(e) => handleRmChange(d.id, "split_rm_id", e.target.value || null)}
                          className="text-xs border border-slate-200 rounded px-2 py-1 bg-white text-slate-700 max-w-[150px]"
                        >
                          <option value="">(none)</option>
                          {Object.entries(members)
                            .sort(([, a], [, b]) => a.localeCompare(b))
                            .map(([id, name]) => (
                              <option key={id} value={id}>{name}</option>
                            ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Floating mass update bar */}
      {selectMode && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-white rounded-xl shadow-2xl border border-slate-200 px-5 py-3 flex items-center gap-3 z-50">
          <button
            onClick={() => {
              if (selectedIds.size === filtered.length && filtered.length > 0) {
                setSelectedIds(new Set());
              } else {
                setSelectedIds(new Set(filtered.map((d) => d.id)));
              }
            }}
            className="text-xs text-bardin-teal font-medium hover:underline"
          >
            {selectedIds.size === filtered.length && filtered.length > 0 ? "Deselect All" : "Select All"}
          </button>
          <span className="text-xs text-slate-500">
            {selectedIds.size} selected
          </span>
          <select
            value={massStage}
            onChange={(e) => setMassStage(e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-white text-slate-700"
          >
            <option value="">Move to…</option>
            {ALL_STAGE_OPTIONS.map((group) => (
              <optgroup key={group.phase} label={group.phase}>
                {group.stages.map((s) => (
                  <option key={s} value={s}>
                    {STAGE_LABELS[s] ?? s}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <button
            onClick={applyMassUpdate}
            disabled={!massStage || selectedIds.size === 0 || massUpdating}
            className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-bardin-teal text-white hover:bg-bardin-teal/90 transition disabled:opacity-40"
          >
            {massUpdating ? "Updating…" : "Apply"}
          </button>
        </div>
      )}
    </div>
  );
}
